import React, { useState } from 'react';
import { motion } from 'framer-motion';

const rawApiBaseUrl = (process.env.REACT_APP_API_BASE_URL || '').trim();
const envApiBaseUrl = rawApiBaseUrl.replace(/\/+$/, '');

/** Cloudflare Worker that serves /api/waitlist and /api/contact (D1). */
const DEFAULT_WORKER_API_BASE = 'https://bonksite.schitturi1.workers.dev';

function resolveApiBaseUrl() {
  if (envApiBaseUrl) return envApiBaseUrl;
  if (typeof window !== 'undefined') {
    const h = (window.location.hostname || '').toLowerCase();
    if (h === 'www.bonkmusic.app' || h === 'bonkmusic.app') {
      return DEFAULT_WORKER_API_BASE;
    }
  }
  return '';
}

const apiBaseUrl = resolveApiBaseUrl();

/** Set true to show the “Contact the team” card next to the waitlist. */
const SHOW_CONTACT_FORM = false;

function buildApiUrl(pathname) {
  if (!pathname.startsWith('/')) {
    throw new Error(`Expected absolute API path, got: ${pathname}`);
  }
  return apiBaseUrl ? `${apiBaseUrl}${pathname}` : pathname;
}

async function readResponseError(response, fallbackMessage) {
  try {
    const data = await response.json();
    if (data && typeof data.error === 'string' && data.error.trim()) {
      return data.error.trim();
    }
  } catch {
    // Ignore parse errors and keep fallback message.
  }
  return fallbackMessage;
}

function WaitlistForm() {
  const [form, setForm] = useState({ email: '', name: '', role: '', notes: '' });
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');
  const source = 'bonk-site-refresh';

  const onChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setStatus('loading');
    setMessage('');

    try {
      const response = await fetch(buildApiUrl('/api/waitlist'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: form.email.trim(),
          name: form.name.trim(),
          role: form.role.trim(),
          notes: form.notes.trim(),
          source,
        }),
      });

      if (!response.ok) {
        const serverError = await readResponseError(response, 'Could not submit waitlist request. Please try again.');
        throw new Error(serverError);
      }

      setStatus('success');
      setMessage('You are on the waitlist.');
      setForm({ email: '', name: '', role: '', notes: '' });
    } catch (error) {
      setStatus('error');
      setMessage(error?.message || 'Could not submit waitlist request. Please try again.');
    }
  };

  return (
    <div className="form-card waitlist-card">
      <h3 className="form-card-title">Join the waitlist</h3>
      <p className="form-card-desc">
        Get notified when Bonk launches and when major features ship.
      </p>
      <form onSubmit={onSubmit}>
        <label className="form-label" htmlFor="waitlist-email">Email</label>
        <input
          id="waitlist-email"
          name="email"
          type="email"
          className="form-input"
          value={form.email}
          onChange={onChange}
          placeholder="you@example.com"
          required
        />

        <div className="lead-inline-fields">
          <div>
            <label className="form-label" htmlFor="waitlist-name">Name (optional)</label>
            <input
              id="waitlist-name"
              name="name"
              className="form-input"
              value={form.name}
              onChange={onChange}
              placeholder="Your name"
            />
          </div>
          <div>
            <label className="form-label" htmlFor="waitlist-role">Role (optional)</label>
            <select
              id="waitlist-role"
              name="role"
              className="form-input"
              value={form.role}
              onChange={onChange}
            >
              <option value="">Select role (optional)</option>
              <option value="dj">DJ</option>
              <option value="producer">Producer</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>

        <label className="form-label form-label--spaced" htmlFor="waitlist-notes">Notes (optional)</label>
        <textarea
          id="waitlist-notes"
          name="notes"
          className="form-input form-textarea"
          value={form.notes}
          onChange={onChange}
          rows={3}
          placeholder="Anything we should know?"
        />

        <input type="hidden" name="source" value={source} />
        <button
          type="submit"
          className="form-submit form-submit--primary"
          disabled={status === 'loading'}
        >
          {status === 'loading' ? 'Submitting...' : 'Join waitlist'}
        </button>
      </form>
      {message ? (
        <p
          role="status"
          aria-live="polite"
          className={`form-message ${status === 'error' ? 'form-message--error' : 'form-message--success'}`}
        >
          {message}
        </p>
      ) : null}
    </div>
  );
}

function ContactForm() {
  const [form, setForm] = useState({ name: '', email: '', topic: 'General inquiry', message: '' });
  const [status, setStatus] = useState('idle');
  const [responseMessage, setResponseMessage] = useState('');
  const source = 'bonk-site-refresh';

  const onChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setStatus('loading');
    setResponseMessage('');

    try {
      const response = await fetch(buildApiUrl('/api/contact'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          topic: form.topic.trim(),
          message: form.message.trim(),
          source,
        }),
      });

      if (!response.ok) {
        const serverError = await readResponseError(response, 'Could not send message. Please try again.');
        throw new Error(serverError);
      }

      setStatus('success');
      setResponseMessage('Message sent. We will reply soon.');
      setForm({ name: '', email: '', topic: 'General inquiry', message: '' });
    } catch (error) {
      setStatus('error');
      setResponseMessage(error?.message || 'Could not send message. Please try again.');
    }
  };

  return (
    <div className="form-card">
      <h3 className="form-card-title">Contact the team</h3>
      <p className="form-card-desc">
        Questions about Bonk workflows, roadmap, or partnerships.
      </p>
      <form onSubmit={onSubmit}>
        <label className="form-label" htmlFor="contact-name">Name</label>
        <input id="contact-name" name="name" className="form-input" value={form.name} onChange={onChange} required />

        <label className="form-label form-label--spaced" htmlFor="contact-email">Email</label>
        <input id="contact-email" type="email" name="email" className="form-input" value={form.email} onChange={onChange} required />

        <label className="form-label form-label--spaced" htmlFor="contact-topic">Topic</label>
        <input
          id="contact-topic"
          name="topic"
          className="form-input"
          value={form.topic}
          onChange={onChange}
          required
          placeholder="General inquiry"
        />

        <label className="form-label form-label--spaced" htmlFor="contact-message">Message</label>
        <textarea
          id="contact-message"
          name="message"
          className="form-input form-textarea form-textarea--lg"
        />

        <input type="hidden" name="source" value={source} />
        <button
          type="submit"
          className="form-submit form-submit--secondary"
          disabled={status === 'loading'}
        >
          {status === 'loading' ? 'Sending...' : 'Send message'}
        </button>
      </form>
      {responseMessage ? (
        <p
          role="status"
          aria-live="polite"
          className={`form-message ${status === 'error' ? 'form-message--error' : 'form-message--success'}`}
        >
          {responseMessage}
        </p>
      ) : null}
    </div>
  );
}

export default function LeadCapture() {
  return (
    <section id="contact" className="section section--sm section--border-top">
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6 }}
        >
          <span className="eyebrow">Stay in the loop</span>
          <h2 className="section-title">Join the waitlist</h2>
          <p className="section-lead">Get release updates and feature announcements.</p>
        </motion.div>

        <div className={`lead-capture-grid${SHOW_CONTACT_FORM ? ' lead-capture-grid--dual' : ''}`}>
          <WaitlistForm />
          {SHOW_CONTACT_FORM ? <ContactForm /> : null}
        </div>
      </div>
    </section>
  );
}
